import React, { useState, useEffect, useContext, useRef, Fragment } from 'react';
import useHubConnection from '../../hooks/useHubConnection';
import { useHistory, useParams, useLocation } from 'react-router-dom';

import { getCookie } from '../../utils/cookie';

import { ProjectsContext } from '../../providers/projects-context.provider';
import { Context } from '../../providers/global-context.provider';
import { IssuesContext } from '../../providers/issues-context.provider';
import { SprintsContext } from '../../providers/sprints-context.provider';

import { initialStatuses } from '../../data/constants';
import { processBacklogIssuesCollections, getNewIssuesCollections } from '../../utils/issues';
import { getProjectDetails } from '../../services/projects.service';
import { updateIssue } from '../../services/issues.service';

import CreateIssueModal from '../../components/create-issue-modal';
import IssueEditModal from '../../components/issue-edit-modal';
import EditSprintModal from '../../components/edit-sprint-modal';
import StartSprintModal from '../../components/start-sprint-modal';

import Alert from '../../components/alert';
import DashboardNavBar from '../../components/dashboard-navbar';
import Spinner from '../../components/spinner';
import MainWrapper from '../../components/main-wrapper';
import PageTopicContainer from '../../components/page-topic-container';
import BacklogDndContainer from '../../components/backlog-dnd-container';
import NoPlanedSprint from '../../components/no-planed-sprint';
import BacklogIssueCard from '../../components/backlog-issue-card';

const BacklogPage = () => {
	const [ isLoading, setLoading ] = useState(true);
	const [ dragging, setDragging ] = useState(false);
	const { toggleModalIsOpen } = useContext(Context);
	const { saveCurrentProject, currentProject } = useContext(ProjectsContext);
	const {
		backlogIssuesCollections,
		creating,
		openedIssue,
		toggleUpdating,
		saveOpenedIssue,
		updateBacklogIssues,
		updateBoardIssues
	} = useContext(IssuesContext);
	const { currentSprint, updatingSprint, startingSprint, saveCurrentSprint } = useContext(SprintsContext);
	const { update } = useHubConnection([
		{ funcName: 'BacklogUpdate' },
		{
			funcName: 'DeletedProjectUpdate',
			successFunc: () => history.push(`/user/projects`)
		}
	]);
	const history = useHistory();
	const { projectId } = useParams();
	const { state } = useLocation();

	const dragItem = useRef();
	const dragItemNode = useRef();
	const movingItem = useRef();
	const showAlert = state ? state.showAlert : false;

	useEffect(
		() => {
			const getCurrentProjectDetails = async () => {
				const token = getCookie('x-auth-token');
				const response = await getProjectDetails(projectId, token);
				const { error } = response;
				if (error) {
					history.push('/404');
					return;
				}

				if (response) {
					const issuesCollections = processBacklogIssuesCollections(response);
					updateBacklogIssues(issuesCollections);
					updateBoardIssues(initialStatuses);
					const activeSprint = response.Sprints.filter((x) => x.Active)[0];
					saveCurrentProject({
						Id: response.Id,
						Name: response.Name,
						Key: response.Key,
						IsCreator: response.IsCreator,
						ActiveSprintId: activeSprint ? activeSprint.Id : null
					});
				}
			};
			getCurrentProjectDetails();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[ update ]
	);

	useEffect(
		() => {
			if (currentProject && currentProject.Id === projectId) {
				setLoading(false);
			}
		},
		[ currentProject, projectId ]
	);

	const handleDragStart = (e, item) => {
		// console.log('handleDragStart',item);
		dragItemNode.current = e.target;
		dragItemNode.current.addEventListener('dragend', handleDragEnd);
		dragItem.current = item;
		movingItem.current = item.issue;
		setTimeout(() => {
			setDragging(true);
		}, 0);
	};

	const handleDragEnter = (e, targetItem) => {
		if (dragItemNode.current !== e.target) {
			const newBacklogCollection = getNewIssuesCollections(backlogIssuesCollections, dragItem, targetItem);
			updateBacklogIssues(newBacklogCollection);
			dragItem.current = targetItem;
		}
	};

	const handleDragEnd = async () => {
		const token = getCookie('x-auth-token');

		setDragging(false);
		console.log('movingItem.current',movingItem.current);
		const data = {
			...movingItem.current,
			AssigneeId: movingItem.current.Assignee.Id,
			BacklogIndex: dragItem.current.index,
			SprintId: dragItem.current.SprintId ? dragItem.current.SprintId : null
		};
		// console.log('data',token);
		await updateIssue(data, token, currentProject.Id);
		dragItem.current = null;
		dragItemNode.current.removeEventListener('dragend', handleDragEnd);
		dragItemNode.current = null;
	};

	const onOpen = (issue, parentIndex) => {
		saveCurrentSprint({ index: parentIndex });
		saveOpenedIssue(issue);
		toggleUpdating();
		toggleModalIsOpen();
	};

	const getEstimate = (index) => {
		const issues = backlogIssuesCollections[index].Issues;
		const estimate = issues.reduce((acc, curr) => {
			return curr.StoryPoints + acc;
		}, 0);

		return estimate;
	};

	const getInvisible = (params) => {
		const currentItem = dragItem.current;
		return currentItem.parentIndex === params.parentIndex && currentItem.index === params.index;
	};

	const renderIssues = (issues, parentIndex, SprintId) =>
	{
		
		// console.log('issues',issues);
		// if(issues !== undefined){
			return issues.map((issue, index) => {
			
				return (
					<Fragment key={issue.Id}>
						<BacklogIssueCard
							issue={issue}
							projectKey={currentProject.Key}
							handleClick={() => onOpen(issue, parentIndex)}
							handleDragStart={(e) => handleDragStart(e, { parentIndex, index, issue })}
							handleDragEnter={
								dragging ? (
									(e) => handleDragEnter(e, { parentIndex, index, IssueId: issue.Id, SprintId })
								) : null
							}
							invisible={dragging ? getInvisible({ parentIndex, index }) : false}
						/>
					</Fragment>
				);
			});
		// }
		
	};

	const renderSprints = () =>
	{
		return backlogIssuesCollections.map((sprint, parentIndex) => {			
			const issues = sprint.Issues;
			return (
				<BacklogDndContainer
					key={sprint.Id ? (sprint.Id+'') : (currentProject.Id+'')}
					sprint={parentIndex === backlogIssuesCollections.length - 1 ? null : sprint}
					sprintIndex={parentIndex}
					issuesCount={issues !== undefined && issues.length >0 ? issues.length : 0}
					estimate={issues !== undefined && issues.length >0 ? getEstimate(parentIndex) : 0}
					onDragEnter={
						dragging && sprint.Issues !== undefined ? (
							(e) => handleDragEnter(e, { parentIndex, index: 0, SprintId: sprint.Id })
						) : null
					}
				>
					{
						issues !== undefined && issues.length >0 ? (
							renderIssues(sprint.Issues, parentIndex, sprint.Id)
						) : parentIndex === 0 ? (
							<NoPlanedSprint />
						) : parentIndex === backlogIssuesCollections.length - 1 ? (
							'بک لاگ خالی است'
						) : (
							'با کشیدن پاورقی اسپرینت به زیر برخی از مسائل، یا با کشیدن مشکلات به اینجا، یک اسپرینت برنامه ریزی کنید. '
						)
					}
				</BacklogDndContainer>
			);
		});
	};

	if (isLoading) {
		return <Spinner color="green-400" />;
	}

	// console.log('==================================================');

	return (
		<Fragment>
			<DashboardNavBar otherClasses="w-full" />
			<MainWrapper>
				<div className="px-16 pt-6 justify-evenly">
					<Alert
						show={showAlert}
						onClose={() => {
							history.push(`/user/dashboard/${currentProject.Id}/backlog`, { showAlert: false });
						}}
					/>
				</div>
				<PageTopicContainer size="lg:w-5/6" title={`بک لاگ / ${!currentProject ? '' : currentProject.Name}`} />
				<div className="overflow-y-auto h-screen">
					<div id="customTag">
					{renderSprints()}
					</div>
					
					{startingSprint ? <StartSprintModal /> : null}
					{!openedIssue ? null : <IssueEditModal item={openedIssue} />}
					{creating ? <CreateIssueModal sprintId={!currentSprint ? null : currentSprint.Id} /> : null}
					{updatingSprint ? <EditSprintModal /> : null}
				</div>
			</MainWrapper>
		</Fragment>
	);
};

export default BacklogPage;
